import { useEffect, useState } from 'react';
import { Alert, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { RoleCardRole } from '../../types';
import { RoleService } from '~/app/services/authorization/role.service';
import { resolveSystemRoleLabel } from '../../utils';
import { RoleTypeGroupedSelect } from '../RoleTypeGroupedSelect/RoleTypeGroupedSelect';
import { ConfirmDialog } from '~/app/components/common/ConfirmDialog/ConfirmDialog';

type DeleteRoleFormValues = {
  fallbackRoleId?: string;
};

type DeleteRoleModalProps = {
  open: boolean;
  organizationId: string;
  role: RoleCardRole | null;
  roles: RoleCardRole[];
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (roleId: string, fallbackRoleId?: string) => Promise<void>;
};

export function DeleteRoleModal({
  open,
  organizationId,
  role,
  roles,
  loading,
  onOpenChange,
  onConfirm,
}: DeleteRoleModalProps) {
  const { t } = useTranslation('authorization');
  const [form] = Form.useForm<DeleteRoleFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const impactQuery = useQuery({
    queryKey: ['role-deletion-impact', organizationId, role?.id],
    queryFn: () => RoleService.findRoleDeletionImpact(organizationId, role!.id),
    enabled: open && Boolean(role?.id),
  });

  useEffect(() => {
    if (!open) {
      form.resetFields();
    }
  }, [form, open]);

  const usersCount = impactQuery.data?.usersCount ?? role?.usersCount ?? 0;
  const requiresFallback = usersCount > 0;
  const roleLabel = role ? resolveSystemRoleLabel(role.name, t) : '';

  const handleConfirm = async () => {
    if (!role) return;

    setSubmitting(true);
    try {
      if (requiresFallback) {
        const values = await form.validateFields();
        await onConfirm(role.id, values.fallbackRoleId);
      } else {
        await onConfirm(role.id);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      variant="danger"
      title={t('deleteModal.title')}
      description={t('deleteModal.body', { name: roleLabel })}
      confirmLabel={t('deleteModal.confirm')}
      cancelLabel={t('deleteModal.cancel')}
      loading={loading || submitting || impactQuery.isLoading}
      onCancel={() => onOpenChange(false)}
      onConfirm={handleConfirm}
    >
      {requiresFallback && (
        <>
          <Alert
            type="warning"
            showIcon
            className="app-confirm-dialog-alert"
            message={t('deleteModal.usersAssigned', { count: usersCount })}
          />

          <Form
            form={form}
            layout="vertical"
            className="app-confirm-dialog-form"
          >
            <Form.Item
              name="fallbackRoleId"
              label={t('deleteModal.fallbackLabel')}
              rules={[
                { required: true, message: t('deleteModal.fallbackRequired') },
              ]}
            >
              <RoleTypeGroupedSelect
                roles={roles}
                excludeRoleId={role?.id}
                placeholder={t('deleteModal.fallbackPlaceholder')}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </ConfirmDialog>
  );
}
